'use client';

import { useState, useEffect, useRef } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { Skeleton, toast } from '@mokoboko/components';

import { verifyResetPasswordToken } from '@/services/auth';
import useTenant from '@/state/tenant';
import useAppearanceSettings from '@/state/appearance-settings';
import {
  LoginDialog,
  CreateNewPasswordDialog,
  CreateAccountDialog,
  TenantDetails,
} from '@/components';
import { UnionPattern } from '@/icons';
import { readUrlCredential, resolveRedirectPath } from '@/lib/utils';
import { GENERIC_ERROR_MESSAGE, RESET_TOKEN_KEY } from '@/lib/constants';

export const Header = () => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { data: tenant, isLoading: isTenantLoading } = useTenant();
  const { isLoading: isAppearanceLoading } = useAppearanceSettings();
  const isLoading = isTenantLoading || isAppearanceLoading;

  const [activeModal, setActiveModal] = useState<
    'login' | 'createNewPassword' | 'createAccount' | null
  >(null);
  const hasValidatedToken = useRef(false);
  const [resetToken, setResetToken] = useState<string | null>(null);

  const action = searchParams?.get('action');
  const postLoginPath = resolveRedirectPath(searchParams?.get('redirect'));

  const checkResetPasswordValidity = async (token: string) => {
    try {
      const response = await verifyResetPasswordToken(token);
      if (response?.valid) {
        setActiveModal('createNewPassword');
      } else {
        router.replace('/');
      }
    } catch (error: any) {
      toast.error(
        error?.message || GENERIC_ERROR_MESSAGE
      );
      router.replace('/');
    }
  };

  useEffect(() => {
    if (action === 'reset-password' && !hasValidatedToken.current) {
      const token = readUrlCredential('token', RESET_TOKEN_KEY);
      if (!token) return;
      hasValidatedToken.current = true;
      setResetToken(token);
      checkResetPasswordValidity(token);
    } else if (action === 'login') {
      setActiveModal('login');
    }
  }, [action]);

  if (isLoading || !tenant) {
    return <HeaderSkeleton />;
  }

  return (
    <header className="relative mx-auto flex w-full flex-col gap-5 overflow-hidden bg-white p-5 shadow-xs sm:rounded-2xl md:max-w-[800px]">
      <div className="pointer-events-none absolute -right-[287px] -top-[120px] z-0 hidden h-[328px] w-[580px] sm:flex sm:items-center sm:justify-center">
        <UnionPattern />
      </div>

      <div className="relative z-10">
        <TenantDetails
          tenant={tenant}
          onLoginClick={() => setActiveModal('login')}
        />
      </div>

      {activeModal === 'login' && (
        <LoginDialog
          open={activeModal === 'login'}
          onClose={() => setActiveModal(null)}
          onConfirm={() => {
            router.replace(postLoginPath);
            setActiveModal(null);
          }}
          onCreateAccountLinkClick={() => setActiveModal('createAccount')}
        />
      )}

      {activeModal === 'createAccount' && (
        <CreateAccountDialog
          open={activeModal === 'createAccount'}
          onClose={() => setActiveModal(null)}
          onLoginLinkClick={() => setActiveModal('login')}
          onConfirm={async () => {
            toast.success(`Welcome to ${tenant?.name}!`, {
              description: "Let's book your first class",
            });
            router.push(postLoginPath);
            setActiveModal(null);
          }}
        />
      )}

      {activeModal === 'createNewPassword' && resetToken && (
        <CreateNewPasswordDialog
          open={activeModal === 'createNewPassword'}
          onClose={() => setActiveModal(null)}
          token={resetToken}
        />
      )}
    </header>
  );
};

export const HeaderSkeleton = () => {
  return (
    <header className="relative mx-auto flex w-full flex-col gap-5 overflow-hidden bg-white p-5 shadow-xs sm:rounded-2xl md:max-w-[800px]">
      <div className="relative z-10">
        <div className="flex items-center gap-3">
          <Skeleton isCircle className="!h-10 !w-10 sm:!h-12 sm:!w-12" />
          <Skeleton text="body-lg" width={180} />
          <Skeleton isRounded className="ml-auto !h-9 w-[88px] shrink-0" />
        </div>

        <div className="mt-2 flex flex-col gap-1">
          <Skeleton text="body-sm" />
          <Skeleton text="body-sm" className="w-2/3" />
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2.5 sm:gap-3">
          <Skeleton height={32} width={176} />
          <Skeleton height={32} width={136} />
          <Skeleton height={32} width={152} />
        </div>
      </div>
    </header>
  );
};
